/** AI generation runs (`Restech_Catalogue_Run`), newest first: tenant-wide (the Runs tab, with
 *  status / trigger / accepted filters) or one product's history (the settings drawer). Each
 *  run records how it was parameterised; a row opens the run detail (host-rendered). */
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material';
import { DataGridPro, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as catalogueApi from '@/api/catalogue';
import { catalogueKeys, type CatalogueScope, type RunFilters, type RunSummary } from '@/api/catalogue';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { formatCost, syncStatusTone } from './catalogueModel';

const STATUSES = ['ok', 'failed', 'truncated', 'budget'] as const;
const TRIGGERS = ['ops_sync', 'webhook', 'ai_force', 'listing'] as const;
const TRIGGER_LABELS: Record<string, string> = {
  ops_sync: 'ops sync',
  webhook: 'webhook',
  ai_force: 'forced AI re-plan',
  listing: 'listing refresh',
};

export function triggerLabel(t: string | undefined): string {
  return t ? (TRIGGER_LABELS[t] ?? t) : '—';
}

export interface RunsTableProps {
  tenantId: number;
  /** One product's history; omit for the tenant-wide list (then `scope` narrows it). */
  productId?: number;
  scope?: CatalogueScope;
  onOpen: (run: RunSummary) => void;
}

export function RunsTable({ tenantId, productId, scope, onOpen }: RunsTableProps) {
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [status, setStatus] = useState('');
  const [trigger, setTrigger] = useState('');
  const [accepted, setAccepted] = useState('');
  const filters: RunFilters = {
    ...(productId != null ? { product_id: productId } : {}),
    ...(productId == null && scope ? { integration: scope.integration, account: scope.account } : {}),
    ...(status ? { status } : {}),
    ...(trigger ? { trigger } : {}),
    ...(accepted ? { accepted: accepted === 'true' } : {}),
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  };
  const query = useQuery({
    queryKey: catalogueKeys.runs(tenantId, filters as Record<string, unknown>),
    queryFn: ({ signal }) => catalogueApi.listRuns(tenantId, filters, signal),
    placeholderData: keepPreviousData,
    retry: false,
  });

  const columns = useMemo<GridColDef<RunSummary>[]>(
    () => [
      {
        field: 'created_ts',
        headerName: 'When',
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <span title={formatDate(row.created_ts)}>{formatRelativeTime(row.created_ts)}</span>
        ),
      },
      ...(productId == null
        ? [
            {
              field: 'product',
              headerName: 'Product',
              flex: 1,
              minWidth: 200,
              sortable: false,
              valueGetter: (_v: unknown, row: RunSummary) => row.product?.name ?? '',
            } as GridColDef<RunSummary>,
          ]
        : []),
      { field: 'stage', headerName: 'Stage', width: 110, sortable: false },
      {
        field: 'trigger',
        headerName: 'Trigger',
        width: 140,
        sortable: false,
        valueGetter: (_v, row) => triggerLabel(row.trigger),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={`${row.status}${row.accepted ? ' · imported' : ''}`}
            color={syncStatusTone(row.status)}
            variant="outlined"
            sx={{ height: 20, fontSize: 11 }}
          />
        ),
      },
      { field: 'model', headerName: 'Model', width: 160, sortable: false },
      {
        field: 'cost_usd',
        headerName: 'Cost',
        width: 80,
        sortable: false,
        valueGetter: (_v, row) => formatCost(row.cost_usd),
      },
      {
        field: 'bgtask',
        headerName: 'Task',
        width: 80,
        sortable: false,
        renderCell: ({ row }) =>
          row.bgtask ? (
            <RouterLink to={`/bgtasks/${row.bgtask}`} onClick={(e) => e.stopPropagation()}>
              task
            </RouterLink>
          ) : null,
      },
    ],
    [productId],
  );

  return (
    <Box>
      {productId == null && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="">Any</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Trigger" value={trigger} onChange={(e) => setTrigger(e.target.value)} sx={{ width: 180 }}>
            <MenuItem value="">Any</MenuItem>
            {TRIGGERS.map((t) => (
              <MenuItem key={t} value={t}>
                {triggerLabel(t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Imported" value={accepted} onChange={(e) => setAccepted(e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="true">Imported</MenuItem>
            <MenuItem value="false">Not imported</MenuItem>
          </TextField>
        </Box>
      )}
      {query.isError ? (
        <Typography color="error" variant="body2">
          {(query.error as Error).message}
        </Typography>
      ) : (
        <DataGridPro<RunSummary>
          rows={query.data?.results ?? []}
          rowCount={query.data?.count ?? 0}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          autoHeight
          density="compact"
          pagination
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50]}
          disableColumnFilter
          disableRowSelectionOnClick
          onRowClick={({ row }) => onOpen(row)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No AI runs yet{productId == null ? '' : ' for this product'}.
              </Box>
            ),
          }}
          slotProps={{ loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' } }}
        />
      )}
    </Box>
  );
}
